// [NEW IMPLEMENTATION] Redux API Slice: Session Management and Re-authentication Logic
// Logic:
// 1. Detects session failures (401, 403, TokenExpired) globally.
// 2. Implements transparent token refreshing (baseQueryWithReauth).
// 3. Manages secure token storage and automatic logout/redirect.
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiRoutes, baseURL } from "../../config/api";
import { logOut, setCredentials } from "../features/auth/authSlice";
import { storage } from "../../utils/storage";
import { Page } from "../../routes/config";

const redirectToLogin = () => {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== Page.LOGIN) {
    window.location.replace(Page.LOGIN);
  }
};

const forceLogoutAndRedirect = (api: any) => {
  api.dispatch(logOut({}));
  storage.clearAll();
  redirectToLogin();
};

const isSessionAuthFailure = (result: any) => {
  const status: number | undefined = result?.error?.status;
  const errType: string | undefined = result?.error?.data?.error?.errType;
  const errMessage: string | undefined =
    result?.error?.data?.error?.message ??
    result?.error?.data?.message ??
    result?.error?.error;

  if (errType === "TokenExpiredError" || errType === "AccessTokenExpiredError") return true;

  if (status === 401) return true;

  if (status === 403) {
    if (typeof errMessage === "string" && /(token.*expired|invalid token|jwt)/i.test(errMessage)) return true;
    return false;
  }

  // [LOGIC] Check if the error indicates a session/authentication issue (Expired, Unauthorized, Forbidden-Token-Invalid)
  if (typeof errMessage === "string" && /(token.*expired|unauthori[sz]ed|invalid token|jwt)/i.test(errMessage)) {
    return true;
  }
  return false;
};

const baseQuery = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as any;
    const token = state?.auth?.accessToken || storage.getToken();
    
    if (token) {
      headers.set("access-token", token);
    }
    
    const idToken = state?.auth?.idToken || storage.getIdToken();
    if (idToken) {
      headers.set("id-token", idToken);
    }
    
    const refreshToken = state?.auth?.refreshToken || storage.getRefreshToken();
    if (refreshToken) {
      headers.set("refresh-token", refreshToken);
    }

    const tenantFromState = state?.auth?.tenantInfo;
    const tid =
      tenantFromState?.id ??
      tenantFromState?.tenantId ??
      storage.getTenantInfo()?.id ??
      storage.getTenantInfo()?.tenantId;
    if (tid) {
      headers.set("tenant-id", String(tid));
    }
    
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result: any = await baseQuery(args, api, extraOptions);

  const httpStatus: number | undefined = result?.error?.status;
  const errType: string | undefined = result?.error?.data?.error?.errType;

  // Only attempt refresh on session/auth failures or explicit token-expired signals.
  // IMPORTANT: do NOT refresh on generic 400 BadRequest errors (these are often query/param issues).
  const shouldAttemptRefresh = isSessionAuthFailure(result);

  if (shouldAttemptRefresh) {
    console.log("[Auth Debug] Auth failure, attempting refresh...", { httpStatus, errType });
    
    const refreshResult: any = await baseQuery(
      { url: apiRoutes.REFRESH_TOKEN, method: 'GET' },
      api,
      extraOptions
    );
    
    // [LOGIC] If tokens are refreshed, update the state and retry the original query.
    // If refresh fails or returns invalid response, force a logout.
    const tokens = refreshResult?.data?.result;
    if (tokens?.accessToken && tokens?.idToken && tokens?.refreshToken) {
      const { idToken, accessToken, refreshToken } = tokens;
      const user = storage.getUser();
      
      api.dispatch(setCredentials({ 
        user, 
        accessToken, 
        idToken,
        refreshToken 
      }));
      storage.setToken(accessToken);
      storage.setIdToken(idToken);
      storage.setRefreshToken(refreshToken);
      
      // RETRY original request with new credentials
      result = await baseQuery(args, api, extraOptions);

      if (isSessionAuthFailure(result)) {
        forceLogoutAndRedirect(api);
      }
    } else {
      forceLogoutAndRedirect(api);
    }
  }

  // Final check: if still failing auth, redirect to login
  if (isSessionAuthFailure(result)) {
    forceLogoutAndRedirect(api);
  }
  
  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Section1", "Section2", "Section3", "Section4", "Section5", "Reports", "Inventory", "Products"],
  endpoints: () => ({}),
});